import { useState, useEffect, useMemo, useCallback } from 'react';
import { RailwayDataService, Chart3DData, Point3D, Hotspot, AnalysisData } from '../service/railwayDataService';

export interface RailwayData3DOptions {
  chartType: 'hotspots' | 'technical' | 'combined';
  selectedMetrics?: string[];
  filterOptions?: {
    priorities?: string[];
    riskRange?: [number, number];
    defectCountRange?: [number, number];
    kpRange?: [number, number];
  };
  autoRefresh?: boolean;
  refreshInterval?: number;
  hotspotsLimit?: '1-50' | '51-150' | '151-250' | '251-500' | 'unlimited';
}

export interface UseRailwayData3DReturn {
  data: Chart3DData;
  isProcessing: boolean;
  error: string | null;
  statistics: {
    totalPoints: number;
    priorityDistribution: { [key: string]: number };
    averageRiskScore: number;
    maxDefectCount: number;
    dataQuality: 'excellent' | 'good' | 'poor' | 'empty';
  };
  filters: {
    selectedMetrics: string[];
    selectedPriorities: string[];
    riskRange: [number, number];
    defectCountRange: [number, number];
    kpRange: [number, number];
    hotspotsLimit: string;
  };
  actions: {
    updateSelectedMetrics: (metrics: string[]) => void;
    updatePriorityFilter: (priorities: string[]) => void;
    updateRiskRange: (range: [number, number]) => void;
    updateDefectCountRange: (range: [number, number]) => void;
    updateKpRange: (range: [number, number]) => void;
    updateHotspotsLimit: (limit: string) => void;
    resetFilters: () => void;
    refreshData: () => void;
    exportFilteredData: () => Chart3DData;
  };
  metadata: {
    lastUpdated: Date;
    dataSource: string;
    processingTime: number;
    availableMetrics: string[];
    availablePriorities: string[];
    totalHotspots: number;
    filteredHotspots: number;
  };
}

const DEFAULT_METRICS = ['geometry', 'ai_risk', 'wear', 'dynamic', 'fatigue', 'compliance'];
const DEFAULT_PRIORITIES = ['CRITICAL', 'HIGH', 'MEDIUM', 'LOW'];

// Configuración de límites de hotspots
const HOTSPOTS_LIMITS = {
  '1-50': { min: 1, max: 50 },
  '51-150': { min: 51, max: 150 },
  '151-250': { min: 151, max: 250 },
  '251-500': { min: 251, max: 500 },
  'unlimited': { min: 0, max: Infinity }
};

export const useRailwayData3D = (
  hotspots: Hotspot[],
  analysisData: AnalysisData | null,
  options: RailwayData3DOptions
): UseRailwayData3DReturn => {
  
  const [rawData, setRawData] = useState<Chart3DData>({ 
    points: [], 
    axes: { 
      x: { label: 'X', min: 0, max: 100 }, 
      y: { label: 'Y', min: 0, max: 100 }, 
      z: { label: 'Z', min: 0, max: 100 } 
    }, 
    bounds: { minX: 0, maxX: 100, minY: 0, maxY: 100, minZ: 0, maxZ: 100 } 
  });
  
  const [isProcessing, setIsProcessing] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [lastUpdated, setLastUpdated] = useState<Date>(new Date());
  const [processingTime, setProcessingTime] = useState<number>(0);

  // Estados de filtros con valores por defecto mejorados
  const [selectedMetrics, setSelectedMetrics] = useState<string[]>(
    options.selectedMetrics || DEFAULT_METRICS.slice(0, 3)
  );
  const [selectedPriorities, setSelectedPriorities] = useState<string[]>(
    options.filterOptions?.priorities || DEFAULT_PRIORITIES
  );
  const [riskRange, setRiskRange] = useState<[number, number]>(
    options.filterOptions?.riskRange || [0, 1]
  );
  const [defectCountRange, setDefectCountRange] = useState<[number, number]>(
    options.filterOptions?.defectCountRange || [0, 100]
  );
  const [kpRange, setKpRange] = useState<[number, number]>(
    options.filterOptions?.kpRange || [0, 100]
  );
  const [hotspotsLimit, setHotspotsLimit] = useState<string>(
    options.hotspotsLimit || '1-50'
  );

  const {
    chartType,
    autoRefresh = false,
    refreshInterval = 30000
  } = options;

  // Filtrar hotspots basado en criterios y límites
  const filteredHotspots = useMemo(() => {
    if (!hotspots || hotspots.length === 0) return [];

    let filtered = hotspots.filter(hotspot => {
      // Filtro por prioridad
      if (!selectedPriorities.includes(hotspot.recommendation_priority)) return false;
      
      // Filtro por risk score
      const risk = hotspot.risk_score || 0;
      if (risk < riskRange[0] || risk > riskRange[1]) return false;
      
      // Filtro por defect count
      const defects = hotspot.defect_count || 0;
      if (defects < defectCountRange[0] || defects > defectCountRange[1]) return false;
      
      // Filtro por KP (extraer de location)
      const kpMatch = hotspot.location.match(/(\d+\.?\d*)/);
      if (kpMatch) {
        const kp = parseFloat(kpMatch[1]);
        if (kp < kpRange[0] || kp > kpRange[1]) return false;
      }
      
      return true;
    });

    // Aplicar límite de hotspots con priorización inteligente
    if (hotspotsLimit !== 'unlimited') {
      const limit = HOTSPOTS_LIMITS[hotspotsLimit];
      
      // Priorizar por severity y risk score
      filtered = filtered
        .sort((a, b) => {
          const priorityOrder = { 'CRITICAL': 4, 'HIGH': 3, 'MEDIUM': 2, 'LOW': 1 };
          const priorityA = priorityOrder[a.recommendation_priority] || 0;
          const priorityB = priorityOrder[b.recommendation_priority] || 0;
          
          if (priorityA !== priorityB) return priorityB - priorityA;
          return (b.risk_score || 0) - (a.risk_score || 0);
        })
        .slice(0, limit.max);
    }

    return filtered;
  }, [hotspots, selectedPriorities, riskRange, defectCountRange, kpRange, hotspotsLimit]);

  // Procesar datos en base al tipo de gráfico
  const processRawData = useCallback(async () => {
    setIsProcessing(true);
    setError(null);
    const startTime = performance.now();

    try {
      let newData: Chart3DData;

      switch (chartType) {
        case 'hotspots':
          newData = RailwayDataService.convertHotspotsTo3D(filteredHotspots);
          break;
        
        case 'technical':
          newData = RailwayDataService.convertAnalysisTo3D(
            analysisData, 
            filteredHotspots, 
            selectedMetrics
          );
          break;
        
        case 'combined':
          // Combinar ambos tipos de datos
          const hotspotsData = RailwayDataService.convertHotspotsTo3D(filteredHotspots);
          const technicalData = RailwayDataService.convertAnalysisTo3D(
            analysisData, 
            filteredHotspots, 
            selectedMetrics
          );
          
          newData = {
            points: [...hotspotsData.points, ...technicalData.points],
            axes: technicalData.axes.x.label !== 'X' ? technicalData.axes : hotspotsData.axes,
            bounds: {
              minX: Math.min(hotspotsData.bounds.minX, technicalData.bounds.minX),
              maxX: Math.max(hotspotsData.bounds.maxX, technicalData.bounds.maxX),
              minY: Math.min(hotspotsData.bounds.minY, technicalData.bounds.minY),
              maxY: Math.max(hotspotsData.bounds.maxY, technicalData.bounds.maxY),
              minZ: Math.min(hotspotsData.bounds.minZ, technicalData.bounds.minZ),
              maxZ: Math.max(hotspotsData.bounds.maxZ, technicalData.bounds.maxZ)
            }
          };
          break;
        
        default:
          throw new Error(`Tipo de gráfico no soportado: ${chartType}`);
      }

      setRawData(newData);
      setLastUpdated(new Date());
      
      const endTime = performance.now();
      setProcessingTime(endTime - startTime);
      
    } catch (err) {
      console.error('Error procesando datos 3D:', err);
      setError(err instanceof Error ? err.message : 'Error procesando datos');
    } finally {
      setIsProcessing(false);
    }
  }, [filteredHotspots, analysisData, chartType, selectedMetrics]);

  // Aplicar filtros adicionales a datos técnicos
  const filteredData = useMemo((): Chart3DData => {
    if (!rawData.points.length) return rawData;

    const filteredPoints = rawData.points.filter(point => {
      const metadata = point.metadata;

      // Filtro por métricas (solo para datos técnicos)
      if (metadata.metric && !selectedMetrics.includes(metadata.metric)) {
        return false;
      }

      return true;
    });

    return {
      ...rawData,
      points: filteredPoints
    };
  }, [rawData, selectedMetrics]);

  // Calcular estadísticas mejoradas
  const statistics = useMemo(() => {
    const baseStats = RailwayDataService.getDataStatistics(filteredData);
    
    // Determinar calidad de datos basada en múltiples factores
    let dataQuality: 'excellent' | 'good' | 'poor' | 'empty';
    const pointCount = filteredData.points.length;
    const hasValidCoordinates = filteredData.points.some(p => 
      p.metadata.coordinates && !p.metadata.isMockData
    );
    
    if (pointCount === 0) {
      dataQuality = 'empty';
    } else if (pointCount >= 100 && hasValidCoordinates && baseStats.averageRiskScore > 0) {
      dataQuality = 'excellent';
    } else if (pointCount >= 20 && (hasValidCoordinates || baseStats.averageRiskScore > 0)) {
      dataQuality = 'good';
    } else {
      dataQuality = 'poor';
    }

    return {
      ...baseStats,
      dataQuality
    };
  }, [filteredData]);

  // Metadatos mejorados con información adicional
  const metadata = useMemo(() => {
    const availableMetrics = Array.from(
      new Set(rawData.points
        .map(p => p.metadata.metric)
        .filter(Boolean)
      )
    );

    const availablePriorities = Array.from(
      new Set(rawData.points
        .map(p => p.metadata.recommendation_priority)
        .filter(Boolean)
      )
    );

    return {
      lastUpdated,
      dataSource: chartType,
      processingTime,
      availableMetrics: availableMetrics.length > 0 ? availableMetrics : DEFAULT_METRICS,
      availablePriorities: availablePriorities.length > 0 ? availablePriorities : DEFAULT_PRIORITIES,
      totalHotspots: hotspots?.length || 0,
      filteredHotspots: filteredHotspots.length
    };
  }, [rawData, lastUpdated, chartType, processingTime, hotspots, filteredHotspots]);

  // Actions para actualizar filtros con validación
  const updateSelectedMetrics = useCallback((metrics: string[]) => {
    if (metrics.length === 0) {
      console.warn('Al menos una métrica debe estar seleccionada');
      return;
    }
    setSelectedMetrics(metrics);
  }, []);

  const updatePriorityFilter = useCallback((priorities: string[]) => {
    setSelectedPriorities(priorities);
  }, []);

  const updateRiskRange = useCallback((range: [number, number]) => {
    if (range[0] >= range[1]) {
      console.warn('Rango de risk score inválido');
      return;
    }
    setRiskRange(range);
  }, []);

  const updateDefectCountRange = useCallback((range: [number, number]) => {
    if (range[0] >= range[1]) {
      console.warn('Rango de defect count inválido');
      return;
    }
    setDefectCountRange(range);
  }, []);

  const updateKpRange = useCallback((range: [number, number]) => {
    if (range[0] >= range[1]) {
      console.warn('Rango de KP inválido');
      return;
    }
    setKpRange(range);
  }, []);

  const updateHotspotsLimit = useCallback((limit: string) => {
    if (!(limit in HOTSPOTS_LIMITS)) {
      console.warn('Límite de hotspots inválido:', limit);
      return;
    }
    setHotspotsLimit(limit);
  }, []);

  const resetFilters = useCallback(() => {
    setSelectedMetrics(DEFAULT_METRICS.slice(0, 3));
    setSelectedPriorities(DEFAULT_PRIORITIES);
    setRiskRange([0, 1]);
    setDefectCountRange([0, 100]);
    setKpRange([0, 100]);
    setHotspotsLimit('1-50');
  }, []);

  const refreshData = useCallback(() => {
    processRawData();
  }, [processRawData]);

  const exportFilteredData = useCallback((): Chart3DData => {
    return JSON.parse(JSON.stringify(filteredData));
  }, [filteredData]);

  // Efectos con manejo de dependencias optimizado
  useEffect(() => {
    processRawData();
  }, [chartType, filteredHotspots.length, selectedMetrics.join(',')]);

  // Auto refresh con limpieza adecuada
  useEffect(() => {
    if (!autoRefresh) return;

    const interval = setInterval(() => {
      processRawData();
    }, refreshInterval);

    return () => clearInterval(interval);
  }, [autoRefresh, refreshInterval, processRawData]);

  // Log de estadísticas en desarrollo
  useEffect(() => {
    if (process.env.NODE_ENV === 'development') {
      console.log('🎯 Railway3D Stats:', {
        chartType,
        totalPoints: filteredData.points.length,
        dataQuality: statistics.dataQuality,
        hotspotsLimit,
        processingTime: processingTime.toFixed(1) + 'ms'
      });
    }
  }, [filteredData.points.length, statistics.dataQuality, chartType, hotspotsLimit, processingTime]);

  return {
    data: filteredData,
    isProcessing,
    error,
    statistics,
    filters: {
      selectedMetrics,
      selectedPriorities,
      riskRange,
      defectCountRange,
      kpRange,
      hotspotsLimit
    },
    actions: {
      updateSelectedMetrics,
      updatePriorityFilter,
      updateRiskRange,
      updateDefectCountRange,
      updateKpRange,
      updateHotspotsLimit,
      resetFilters,
      refreshData,
      exportFilteredData
    },
    metadata
  };
};